import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { APICONSTANT } from '../constants/api.constant';
import { catchError, map, Observable } from 'rxjs';
import {
  CountryStateCodeMaintenanceResponseData,
  CountryStateCodeMaintenanceRequestData,
  CountryStateCodeMaintenanceData,
} from '../models/country-state-code-maintenance.model';
import {
  ModelCodeMaintenanceData,
  ModelCodeMaintenanceRequestData,
  ModelCodeMaintenanceResponseData,
} from '../models/model-code-maintenance.model';
import {
  ParameterMaintenanceData,
  ParameterMaintenanceRequestData,
  ParameterMaintenanceResponseData,
} from '../models/parameter-maintenance.model';
import {
  RelationshipMaintenanceData,
  RelationshipMaintenanceRequestData,
  RelationshipMaintenanceResponseData,
} from '../models/relationship-maintenance.model';
import {
  ConsentAdministrationConsentResponseData,
  ConsentAdministrationSearchRequestData,
  ConsentAdministrationSearchResponseData,
  ConsentAdministrationUploadConsentRequestData,
  ConsentAdministrationUploadConsentResponseData,
  ConsentAdministrationViewConsentResponseData,
} from '../models/consent-administration.model';
import { ConsentAdministrationData } from '../models/consent-administration.model';
import {
  MvrScoreData,
  MvrScoreResponseData,
  MvrScoreSearchRequestData,
  MvrScoreSearchResponseData,
  MvrScoreUpdateScoreRequestData,
  MvrScoreUpdateScoreResponseData,
} from '../models/mvr-score.model';
import { VaUserProfileResponseData } from '../models/user-profile.model';
import {
  AllLicenseData,
  CountriesData,
  CountriesResponseData,
  EmailConsentToAuthorizedDriverRequestData,
  EmailConsentToAuthorizedDriverResponseData,
  EmployeeDriverLicenseAndInformationFileResponseData,
  EmployeeDriverLicenseAndInformationFormsResponseData,
  ReactivateAuthorizedDriverRequestData,
  ReactivateAuthorizedDriverResponseData,
  SaveDriverLicenseInTempTableLicenseRequestData,
  SaveDriverLicenseInTempTableLicenseResponseData,
  SaveDriverLicenseRequestData,
  SaveDriverLicenseResponseData,
  StatesData,
  StatesResponseData,
  UploadConsentForAuthorizedDriverFromEmployeeDriverLicenseRequestData,
  UploadConsentForAuthorizedDriverFromEmployeeDriverLicenseResponseData,
} from '../models/employee-driver-license-and-information.model';

import { SharedService } from './shared-service';
import {
  employeeDetailsMvrResponse,
  employeeDetailsPayload,
  UpdateMVRScorePayload,
} from '../models/employee-driver-mvr.model';
import { ImportFilePayload } from '../components/model-code-maintenance/model-code-maintenance';

@Injectable({
  providedIn: 'root',
})
export class BackendService {
  private baseUrl = '';
  private httpClient = inject(HttpClient);
  private sharedService = inject(SharedService);
  constructor() {
    this.checkDomainType();
  }

  /**
   * This function sets the base API URL.
   */
  checkDomainType(): void {
    const currentUrl: string = window.location.href;
    this.baseUrl = 'http://localhost:8090';
    if (currentUrl.includes('.cloud.bmw')) {
      this.baseUrl = environment.intranet.backendBaseUrl;
    } else if (currentUrl.includes('.bmw.cloud')) {
      this.baseUrl = environment.internet.backendBaseUrl;
    } else {
      this.baseUrl = environment.intranet.backendBaseUrl;
    }
  }

  /** ------------ Country State Code Maintenance APIs ------------ */

  getCountryStateCodeMaintenanceData(): Observable<
    CountryStateCodeMaintenanceData[]
  > {
    return this.httpClient
      .get<CountryStateCodeMaintenanceResponseData>(
        this.baseUrl + APICONSTANT.COUNRTY_STATE_MAPPING
      )
      .pipe(
        map((response) => {
          return response.data as CountryStateCodeMaintenanceData[];
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  updateCountryStateCodeMaintenanceData(
    requestBody: CountryStateCodeMaintenanceRequestData[]
  ): Observable<CountryStateCodeMaintenanceResponseData> {
    return this.httpClient
      .put<CountryStateCodeMaintenanceResponseData>(
        this.baseUrl + APICONSTANT.COUNRTY_STATE_MAPPING,
        requestBody
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  /** ------------ ------------ ------------ ------------ ------------ */

  /** ---------------- Relationship Maintenance APIs ---------------- */

  getRelationshipMaintenanceData(): Observable<RelationshipMaintenanceData[]> {
    return this.httpClient
      .get<RelationshipMaintenanceResponseData>(
        this.baseUrl + APICONSTANT.RELATION_TO_EMPLOYEE
      )
      .pipe(
        map((response) => {
          return response.data as RelationshipMaintenanceData[];
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  updateRelationshipMaintenanceData(
    requestBody: RelationshipMaintenanceRequestData[]
  ): Observable<RelationshipMaintenanceResponseData> {
    return this.httpClient
      .put<RelationshipMaintenanceResponseData>(
        this.baseUrl + APICONSTANT.RELATION_TO_EMPLOYEE,
        requestBody
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  /** ------------ ------------ ------------ ------------ ------------ */

  /** ----------------- Model Code Maintenance APIs ------------------ */

  getModelCodeMaintenanceData(): Observable<ModelCodeMaintenanceData[]> {
    return this.httpClient
      .get<ModelCodeMaintenanceResponseData>(
        this.baseUrl + APICONSTANT.MODEL_CODE
      )
      .pipe(
        map((response) => {
          return response.data as ModelCodeMaintenanceData[];
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  updateModelCodeMaintenanceData(
    requestBody: ModelCodeMaintenanceRequestData[]
  ): Observable<ModelCodeMaintenanceResponseData> {
    return this.httpClient
      .put<ModelCodeMaintenanceResponseData>(
        this.baseUrl + APICONSTANT.MODEL_CODE,
        requestBody
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  deleteModelParameterData(
    requestBody: ModelCodeMaintenanceRequestData[]
  ): Observable<null> {
    return this.httpClient.delete<null>(this.baseUrl + APICONSTANT.MODEL_CODE, {
      body: requestBody,
    });
  }

  sendImportedModelCodeFile() {}

  /** ------------ ------------ ------------ ------------ ------------ */

  /** ----------------- Parameter Maintenance APIs ------------------- */

  getParameterMaintenanceData(): Observable<ParameterMaintenanceData[]> {
    return this.httpClient
      .get<ParameterMaintenanceResponseData>(
        this.baseUrl + APICONSTANT.PARAMETER
      )
      .pipe(
        map((response) => {
          return response.data as ParameterMaintenanceData[];
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  updateParameterMaintenanceData(
    requestBody: ParameterMaintenanceRequestData[]
  ): Observable<ParameterMaintenanceResponseData> {
    return this.httpClient
      .put<ParameterMaintenanceResponseData>(
        this.baseUrl + APICONSTANT.PARAMETER,
        requestBody
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  /** ------------ ------------ ------------ ------------ ------------ */

  /** ---------------- MVR Score Maintenance APIs -------------------- */

  searchEmployeeMvrScoreData(
    requestBody: MvrScoreSearchRequestData
  ): Observable<MvrScoreData[]> {
    return this.httpClient
      .get<MvrScoreSearchResponseData>(
        `${this.baseUrl}${APICONSTANT.SEARCH_CONSENT_FOR_EMPLOYEE}?first-name=${
          requestBody.firstName || ''
        }&last-name=${requestBody.lastName || ''}&employee-id=${
          requestBody.employeeId || ''
        }`
      )
      .pipe(
        map((response) => {
          return response.data as MvrScoreData[];
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  getEmployeeAndAuthorizedDriverMvrScoreData(
    employeeId: string
  ): Observable<MvrScoreData[]> {
    return this.httpClient
      .get<MvrScoreResponseData>(
        `${this.baseUrl}${APICONSTANT.MVR_SCORE}${employeeId}`
      )
      .pipe(
        map((response) => {
          return response.data as MvrScoreData[];
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  updateMvrScoreData(
    requestBody: MvrScoreUpdateScoreRequestData
  ): Observable<MvrScoreUpdateScoreResponseData> {
    return this.httpClient
      .put<MvrScoreUpdateScoreResponseData>(
        this.baseUrl + APICONSTANT.MVR_SCORE,
        requestBody
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  /** ------------ ------------ ------------ ------------ ------------ */

  /** ---------------- Consent Administration APIs ------------------- */

  searchEmployeeConsentData(
    requestBody: ConsentAdministrationSearchRequestData
  ): Observable<ConsentAdministrationData[]> {
    return this.httpClient
      .get<ConsentAdministrationSearchResponseData>(
        `${this.baseUrl}${APICONSTANT.SEARCH_CONSENT_FOR_EMPLOYEE}?first-name=${
          requestBody.firstName || ''
        }&last-name=${requestBody.lastName || ''}&employee-id=${
          requestBody.employeeId || ''
        }`
      )
      .pipe(
        map((response) => {
          return response.data as ConsentAdministrationData[];
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  getEmployeeAndAuthorizedDriverConsentData(
    employeeId: string
  ): Observable<ConsentAdministrationData[]> {
    return this.httpClient
      .get<ConsentAdministrationConsentResponseData>(
        `${this.baseUrl}${APICONSTANT.VIEW_CONSENT_DETAILS}${employeeId}`
      )
      .pipe(
        map((response) => {
          return response.data as ConsentAdministrationData[];
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  getAuthorizedDriverConsentFiles(
    employeeId: string,
    customPersonId: string
  ): Observable<ConsentAdministrationViewConsentResponseData> {
    return this.httpClient
      .get<ConsentAdministrationViewConsentResponseData>(
        `${this.baseUrl}${APICONSTANT.VIEW_CONSENT_DETAILS}${employeeId}/custom-persons/${customPersonId}`
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  uploadConsentForAuthorizedDriverFromConsentAdministration(
    requestBody: ConsentAdministrationUploadConsentRequestData
  ): Observable<ConsentAdministrationUploadConsentResponseData> {
    return this.httpClient
      .put<ConsentAdministrationUploadConsentResponseData>(
        this.baseUrl +
          APICONSTANT.UPLOAD_CONSENT_VIA_CONSENT_ADMIN_FOR_AUTHORIZED_USER,
        requestBody
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  /** ------------ ------------ ------------ ------------ ------------ */

  /** ------------------ Employee DL & Info. APIs -------------------- */

  getCountryData(): Observable<CountriesData[]> {
    return this.httpClient
      .get<CountriesResponseData>(this.baseUrl + APICONSTANT.COUNTRIES)
      .pipe(
        map((response) => {
          return response.data as CountriesData[];
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  getStateData(): Observable<StatesData[]> {
    return this.httpClient
      .get<StatesResponseData>(this.baseUrl + APICONSTANT.STATES)
      .pipe(
        map((response) => {
          return response.data as StatesData[];
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  getSummaryOfRightsDocument(): Observable<EmployeeDriverLicenseAndInformationFileResponseData> {
    return this.httpClient
      .get<EmployeeDriverLicenseAndInformationFileResponseData>(
        this.baseUrl +
          '/v1/ccsp/files?filePath=user-rights/A%20Summary%20of%20Your%20Rights.pdf'
      )
      .pipe(
        map((response) => {
          return response as EmployeeDriverLicenseAndInformationFileResponseData;
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  getHowToEnrollGuideDocument(): Observable<EmployeeDriverLicenseAndInformationFileResponseData> {
    return this.httpClient
      .get<EmployeeDriverLicenseAndInformationFileResponseData>(
        this.baseUrl +
          '/v1/ccsp/files?filePath=user-rights/MVR_HOW_TO_ENROLL_CCSP.pdf'
      )
      .pipe(
        map((response) => {
          return response as EmployeeDriverLicenseAndInformationFileResponseData;
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  getAllDriverLicenseData(employeeId: string): Observable<AllLicenseData> {
    return this.httpClient
      .get<EmployeeDriverLicenseAndInformationFormsResponseData>(
        `${this.baseUrl}${APICONSTANT.LICENSE_DETAILS}${employeeId}`
      )
      .pipe(
        map((response) => {
          return response.data as AllLicenseData;
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  saveDriverLicenseDataInTempTable(
    requestBody: SaveDriverLicenseInTempTableLicenseRequestData
  ): Observable<SaveDriverLicenseInTempTableLicenseResponseData> {
    return this.httpClient
      .put<SaveDriverLicenseInTempTableLicenseResponseData>(
        this.baseUrl + APICONSTANT.SAVE_TEMP_LICENSES,
        requestBody
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  emailConsentToAuthorizedDriver(
    requestBody: EmailConsentToAuthorizedDriverRequestData
  ): Observable<EmailConsentToAuthorizedDriverResponseData> {
    return this.httpClient
      .post<EmailConsentToAuthorizedDriverResponseData>(
        this.baseUrl + APICONSTANT.SEND_EMAIL,
        requestBody
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  saveDriverLicenseData(
    requestBody: SaveDriverLicenseRequestData
  ): Observable<SaveDriverLicenseResponseData> {
    return this.httpClient
      .put<SaveDriverLicenseInTempTableLicenseResponseData>(
        this.baseUrl + APICONSTANT.LICENSE_DETAILS,
        requestBody
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  reactivateAuthorizedDriver(
    requestBody: ReactivateAuthorizedDriverRequestData
  ): Observable<ReactivateAuthorizedDriverResponseData> {
    return this.httpClient
      .put<ReactivateAuthorizedDriverResponseData>(
        this.baseUrl + APICONSTANT.REACTIVATE_AUTHORIZED_USER,
        requestBody
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  uploadConsentForAuthorizedDriverFromEmployeeDriverLicense(
    requestBody: UploadConsentForAuthorizedDriverFromEmployeeDriverLicenseRequestData
  ): Observable<UploadConsentForAuthorizedDriverFromEmployeeDriverLicenseResponseData> {
    return this.httpClient
      .put<UploadConsentForAuthorizedDriverFromEmployeeDriverLicenseResponseData>(
        this.baseUrl + APICONSTANT.UPLOAD_CONSENT,
        requestBody
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  /** ------------ ------------ ------------ ------------ ------------ */

  /** ------------------ Common application APIs -------------------- */

  getEmployeeDetailsFromVa(
    employeeNumber: string
  ): Observable<VaUserProfileResponseData> {
    return this.httpClient.get<VaUserProfileResponseData>(
      this.baseUrl + APICONSTANT.EMPLOYEE_NAME + employeeNumber
    );
  }

  /** ------------ ------------ ------------ ------------ ------------ */
  getAllEmployeeDetails(
    payload: employeeDetailsPayload,
    pageNumber: number
  ): Observable<employeeDetailsMvrResponse> {
    return this.httpClient.post<employeeDetailsMvrResponse>(
      this.baseUrl + APICONSTANT.EMPLOYEE_LIST + '?pageNo=' + pageNumber,
      payload
    );
  }

  updateMVRScoreDetails(payload: UpdateMVRScorePayload): Observable<{
    baseResponse: UpdateMVRScorePayload;
    status: string;
    success: string;
  }> {
    return this.httpClient.put<{
      baseResponse: UpdateMVRScorePayload;
      status: string;
      success: string;
    }>(`${this.baseUrl}${APICONSTANT.MVR_SCORE}`, payload);
  }

  exportEmployeeData(payload: employeeDetailsPayload): Observable<any> {
    return this.httpClient.post(
      `${this.baseUrl}${APICONSTANT.EXPORT_EMPLOYEE_LIST}`,
      payload,
      { observe: 'response', responseType: 'blob' }
    );
    // return this.httpClient.get('assets/mock/mock_CSV.csv', {
    //   observe: 'response',
    //   responseType: 'blob',
    // });
  }
  sendImportedFile(
    requestBody: ImportFilePayload[],
    isOverride: boolean
  ): Observable<any> {
    if (isOverride) {
      return this.httpClient.post(
        this.baseUrl + APICONSTANT.IMPORT_MODEL_PAREMETER,
        requestBody
      );
    } else {
      return this.httpClient.put(
        this.baseUrl + APICONSTANT.IMPORT_AND_REPLACE_MODEL_PAREMETER,
        requestBody
      );
    }
  }
}
